import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import MarkdownEditor from '../components/MarkdownEditor';
import {
  DragIndicator,
  Edit,
  Delete,
  Add,
  ExpandMore,
  Work,
  School,
  Person,
  EmojiObjects,
  Assignment,
  VerifiedUser
} from '@mui/icons-material';

/**
 * Edit Resume Tab Component
 * Handles resume content editing with drag-and-drop functionality
 */
const EditResumeTab = ({
  candidateData,
  setCandidateData,
  sectionOrder,
  setSectionOrder,
  onDragEnd,
  addSectionItem,
  updateSectionItem,
  deleteSectionItem,
  editMode,
  setEditMode
}) => {
  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    summary: true,
    experience: true,
    education: true,
    skills: true
  });
  const [editDialogs, setEditDialogs] = useState({});

  const sectionIcons = {
    personal: <Person />,
    summary: <EmojiObjects />,
    experience: <Work />,
    education: <School />,
    skills: <Assignment />,
    certifications: <VerifiedUser />,
    projects: <Assignment />
  };

  const sectionTitles = {
    personal: 'Personal Information',
    summary: 'Professional Summary',
    experience: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    certifications: 'Certifications',
    projects: 'Projects'
  };

  /**
   * Handle section expansion
   */
  const handleSectionExpansion = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  /**
   * Open edit dialog
   */
  const openEditDialog = (section, item = null) => {
    setEditDialogs({
      [section]: {
        open: true,
        item: item || getDefaultItem(section),
        isNew: !item
      }
    });
  };

  /**
   * Close edit dialog
   */
  const closeEditDialog = (section) => {
    setEditDialogs({
      ...editDialogs,
      [section]: { open: false }
    });
  };

  /**
   * Get default item for section
   */
  const getDefaultItem = (section) => {
    const defaults = {
      experience: {
        company: '',
        position: '',
        location: '',
        startDate: '',
        endDate: '',
        description: '',
        skills: [],
        current: false
      },
      education: {
        institution: '',
        degree: '',
        startDate: '',
        endDate: '',
        gpa: '',
        description: ''
      },
      skills: {
        name: '',
        level: 'Intermediate',
        category: 'Technical'
      },
      certifications: {
        name: '',
        issuer: '',
        date: '',
        url: '',
        description: ''
      },
      projects: {
        name: '',
        description: '',
        technologies: [],
        url: '',
        startDate: '',
        endDate: ''
      }
    };
    return defaults[section] || {};
  };

  /**
   * Save item from dialog
   */
  const saveItem = (section) => {
    const dialog = editDialogs[section];
    if (!dialog) return;

    if (dialog.isNew) {
      addSectionItem(section, dialog.item);
    } else {
      updateSectionItem(section, dialog.item.id, dialog.item);
    }
    closeEditDialog(section);
  };

  /**
   * Update dialog item
   */
  const updateDialogItem = (section, updates) => {
    setEditDialogs(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        item: {
          ...prev[section].item,
          ...updates
        }
      }
    }));
  };

  /**
   * Render personal information section
   */
  const renderPersonalSection = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Full Name"
          value={candidateData.personal.name}
          onChange={(e) => setCandidateData(prev => ({
            ...prev,
            personal: { ...prev.personal, name: e.target.value }
          }))}
          disabled={!editMode}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={candidateData.personal.email}
          onChange={(e) => setCandidateData(prev => ({
            ...prev,
            personal: { ...prev.personal, email: e.target.value }
          }))}
          disabled={!editMode}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Phone"
          value={candidateData.personal.phone}
          onChange={(e) => setCandidateData(prev => ({
            ...prev,
            personal: { ...prev.personal, phone: e.target.value }
          }))}
          disabled={!editMode}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Location"
          value={candidateData.personal.location}
          onChange={(e) => setCandidateData(prev => ({
            ...prev,
            personal: { ...prev.personal, location: e.target.value }
          }))}
          disabled={!editMode}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="LinkedIn"
          value={candidateData.personal.linkedin}
          onChange={(e) => setCandidateData(prev => ({
            ...prev,
            personal: { ...prev.personal, linkedin: e.target.value }
          }))}
          disabled={!editMode}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Website"
          value={candidateData.personal.website || ''}
          onChange={(e) => setCandidateData(prev => ({
            ...prev,
            personal: { ...prev.personal, website: e.target.value }
          }))}
          disabled={!editMode}
        />
      </Grid>
    </Grid>
  );

  /**
   * Render summary section
   */
  const renderSummarySection = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom>Professional Summary</Typography>
      {editMode ? (
        <MarkdownEditor
          value={candidateData.summary.content || ''}
          onChange={(content) => setCandidateData(prev => ({
            ...prev,
            summary: { content: content }
          }))}
          minHeight={200}
        />
      ) : (
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Professional Summary"
          value={candidateData.summary.content}
          disabled={true}
        />
      )}
      <Typography variant="caption" color="textSecondary">
        A brief overview of your professional background and key achievements. Supports Markdown formatting.
      </Typography>
    </Box>
  );

  /**
   * Render list section (experience, education, etc.)
   */
  const renderListSection = (section) => {
    const items = candidateData[section] || [];
    
    return (
      <Box>
        {items.map((item, index) => (
          <Card key={item.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6">
                  {section === 'experience' && `${item.position} at ${item.company}`}
                  {section === 'education' && `${item.degree} - ${item.institution}`}
                  {section === 'skills' && item.name}
                  {section === 'certifications' && item.name}
                  {section === 'projects' && item.name}
                </Typography>
                {editMode && (
                  <Box>
                    <IconButton onClick={() => openEditDialog(section, item)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => deleteSectionItem(section, item.id)}>
                      <Delete />
                    </IconButton>
                  </Box>
                )}
              </Box>
              
              {section === 'experience' && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {item.startDate} - {item.endDate || 'Present'} • {item.location}
                  </Typography>
                  {item.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {item.description}
                    </Typography>
                  )}
                  {item.skills && item.skills.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {item.skills.map((skill, skillIndex) => (
                        <Chip key={skillIndex} label={skill} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              
              {section === 'education' && (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    {item.startDate} - {item.endDate}
                  </Typography>
                  {item.gpa && (
                    <Typography variant="body2">GPA: {item.gpa}</Typography>
                  )}
                  {item.description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {item.description}
                    </Typography>
                  )}
                </Box>
              )}
              
              {section === 'skills' && (
                <Box>
                  <Chip label={item.level} color="primary" size="small" />
                  <Chip label={item.category} variant="outlined" size="small" sx={{ ml: 1 }} />
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
        
        {editMode && (
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => openEditDialog(section)}
            fullWidth
            sx={{ mt: 1 }}
          >
            Add {sectionTitles[section].replace(/s$/, '')}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Edit Mode Toggle */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Resume Content</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={editMode}
                onChange={(e) => setEditMode(e.target.checked)}
              />
            }
            label="Edit Mode"
          />
        </Box>
      </Paper>

      {/* Sections */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {sectionOrder.map((section, index) => (
                <Draggable key={section} draggableId={section} index={index}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.draggableProps}>
                      <Accordion
                        expanded={expandedSections[section]}
                        onChange={() => handleSectionExpansion(section)}
                        sx={{ mb: 2 }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{ 
                            '& .MuiAccordionSummary-content': {
                              alignItems: 'center'
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                            {editMode && (
                              <IconButton
                                {...provided.dragHandleProps}
                                sx={{ mr: 1 }}
                                size="small"
                              >
                                <DragIndicator />
                              </IconButton>
                            )}
                            {sectionIcons[section]}
                            <Typography variant="h6" sx={{ ml: 1 }}>
                              {sectionTitles[section]}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {section === 'personal' && renderPersonalSection()}
                          {section === 'summary' && renderSummarySection()}
                          {['experience', 'education', 'skills', 'certifications', 'projects'].includes(section) && 
                            renderListSection(section)}
                        </AccordionDetails>
                      </Accordion>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Edit Dialogs */}
      {Object.entries(editDialogs).map(([section, dialog]) => 
        dialog.open && (
          <ItemEditDialog
            key={section}
            section={section}
            item={dialog.item}
            isNew={dialog.isNew}
            onUpdate={(updates) => updateDialogItem(section, updates)}
            onSave={() => saveItem(section)}
            onClose={() => closeEditDialog(section)}
          />
        )
      )}
    </Box>
  );
};

/**
 * Item Edit Dialog Component
 */
const ItemEditDialog = ({ section, item, isNew, onUpdate, onSave, onClose }) => {
  const getDialogTitle = () => {
    const titles = {
      experience: 'Experience',
      education: 'Education',
      skills: 'Skill',
      certifications: 'Certification',
      projects: 'Project'
    };
    return `${isNew ? 'Add' : 'Edit'} ${titles[section]}`;
  };

  const renderDialogContent = () => {
    switch (section) {
      case 'experience':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Company"
                value={item.company}
                onChange={(e) => onUpdate({ company: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Position"
                value={item.position}
                onChange={(e) => onUpdate({ position: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location"
                value={item.location}
                onChange={(e) => onUpdate({ location: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={item.current}
                    onChange={(e) => onUpdate({ current: e.target.checked })}
                  />
                }
                label="Current Position"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                value={item.startDate}
                onChange={(e) => onUpdate({ startDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                value={item.endDate}
                onChange={(e) => onUpdate({ endDate: e.target.value })}
                disabled={item.current}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Description</Typography>
              <MarkdownEditor
                value={item.description || ''}
                onChange={(content) => onUpdate({ description: content })}
                minHeight={250}
              />
            </Grid>
          </Grid>
        );
      
      case 'education':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Institution"
                value={item.institution}
                onChange={(e) => onUpdate({ institution: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Degree"
                value={item.degree}
                onChange={(e) => onUpdate({ degree: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                value={item.startDate}
                onChange={(e) => onUpdate({ startDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                value={item.endDate}
                onChange={(e) => onUpdate({ endDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="GPA"
                value={item.gpa}
                onChange={(e) => onUpdate({ gpa: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>Description (supports Markdown)</Typography>
              <MarkdownEditor
                value={item.description || ''}
                onChange={(content) => onUpdate({ description: content })}
                minHeight={200}
              />
            </Grid>
          </Grid>
        );
      
      case 'skills':
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Skill Name"
                value={item.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                select
                label="Level"
                value={item.level}
                onChange={(e) => onUpdate({ level: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Category"
                value={item.category}
                onChange={(e) => onUpdate({ category: e.target.value })}
              />
            </Grid>
          </Grid>
        );
      
      default:
        return <Typography>Edit form for {section} coming soon!</Typography>;
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {renderDialogContent()}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditResumeTab;
